'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { useUserDataContext } from '@/components/navigation/contexts/user-data-context';
import { ABOUT_TRIAL_DISMISSED_KEY, TRIAL_HEADER_DISMISSED_KEY } from '@/constants/trial';

const FreeTrialModal = dynamic(() => import('@/components/ui/free-trial-modal'), { ssr: false });

interface TrialContextType {
    /** Whether the user has already used their free trial */
    trialUsed: boolean;
    /** Whether the user is currently in an active trial */
    isTrialing: boolean;
    /** Whether the user has an active paid subscription */
    hasActiveSubscription: boolean;
    isTrialEnded: boolean;
    isTeamMemberNotOwner: boolean;
    /** Whether the user is eligible for trial (matches backend getTrialStatus.eligibleForTrial + !isTeamMemberNotOwner) */
    eligibleForTrial: boolean;
    /** Show the free trial modal. Returns false if trial already used or user is non-owner team member. */
    showTrialModal: () => boolean;
    /** Close the trial modal */
    closeTrialModal: () => void;
    /** Whether the trial modal is currently open */
    isTrialModalOpen: boolean;
    /** Whether the global trial header banner has been dismissed */
    trialHeaderDismissed: boolean;
    /** Dismiss the trial header banner */
    dismissTrialHeader: () => void;
    aboutTrialDismissed: boolean;
    notifyAboutTrialDismissed: () => void;
}

const TrialContext = createContext<TrialContextType | undefined>(undefined);

export const TrialProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { userDetails, hasTeam, isTeamOwner } = useUserDataContext();
    const [isTrialModalOpen, setIsTrialModalOpen] = useState(false);
    const [trialHeaderDismissed, setTrialHeaderDismissed] = useState(false);
    const [aboutTrialDismissed, setAboutTrialDismissed] = useState(false);

    const isOrganizationWorkspace = Boolean(userDetails?.billingOrganizationId);
    const trialUsed = userDetails?.trialUsed ?? false;
    const subscriptionStatus = userDetails?.monthlySubscriptionStatus;
    const isTrialing = !isOrganizationWorkspace && subscriptionStatus === 'trialing';
    const hasActiveSubscription = subscriptionStatus === 'active';
    const trialEndDate = userDetails?.trialEndDate;
    const isTrialEnded =
        !isOrganizationWorkspace && trialUsed &&
        !hasActiveSubscription &&
        (subscriptionStatus === 'paused' ||
            subscriptionStatus === 'past_due' ||
            (trialEndDate != null && new Date(trialEndDate).getTime() <= Date.now()));
    // User is in a team but NOT the owner — they cannot start trials (those
    // provision a brand-new team), though they CAN pay to upgrade the team's
    // plan (handled via the billing flow).
    const isTeamMemberNotOwner = hasTeam && !isTeamOwner;
    // User has or had paid subscription (own or inherited from team) — not eligible for trial
    const paidPlanTypes = ['starter', 'pro', 'advanced'];
    const hasPaidPlan = userDetails?.subscriptionPlanType && paidPlanTypes.includes(userDetails.subscriptionPlanType);
    const hasSubscription = !!userDetails?.subscriptionId;
    // Matches backend getTrialStatus.eligibleForTrial + isTeamMemberNotOwner (team members cannot start trials)
    const eligibleForTrial =
        !isOrganizationWorkspace && !trialUsed &&
        !isTrialing &&
        !hasActiveSubscription &&
        !isTeamMemberNotOwner &&
        !hasPaidPlan &&
        !hasSubscription;

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const headerDismissed = localStorage.getItem(TRIAL_HEADER_DISMISSED_KEY);
            if (headerDismissed === 'true') {
                setTrialHeaderDismissed(true);
            }
            const aboutDismissed = localStorage.getItem(ABOUT_TRIAL_DISMISSED_KEY);
            if (aboutDismissed === 'true') {
                setAboutTrialDismissed(true);
            }
        }
    }, []);

    const notifyAboutTrialDismissed = useCallback(() => {
        setAboutTrialDismissed(true);
    }, []);

    // Reset dismissed state if subscription status changes (e.g., trial ends)
    useEffect(() => {
        if (!isTrialing && trialHeaderDismissed) {
            // If no longer trialing, clear the dismissed state for next time
            if (typeof window !== 'undefined') {
                localStorage.removeItem(TRIAL_HEADER_DISMISSED_KEY);
            }
            setTrialHeaderDismissed(false);
        }
    }, [isTrialing, trialHeaderDismissed]);

    const showTrialModal = useCallback((): boolean => {
        if (isOrganizationWorkspace || trialUsed || isTrialing || hasActiveSubscription || isTeamMemberNotOwner || hasPaidPlan || hasSubscription) {
            return false;
        }
        setIsTrialModalOpen(true);
        return true;
    }, [isOrganizationWorkspace, trialUsed, isTrialing, hasActiveSubscription, isTeamMemberNotOwner, hasPaidPlan, hasSubscription]);

    const closeTrialModal = useCallback(() => {
        setIsTrialModalOpen(false);
    }, []);

    const dismissTrialHeader = useCallback(() => {
        setTrialHeaderDismissed(true);
        if (typeof window !== 'undefined') {
            localStorage.setItem(TRIAL_HEADER_DISMISSED_KEY, 'true');
        }
    }, []);

    const handleTrialStarted = useCallback(() => {
        setIsTrialModalOpen(false);
        // The page will refresh and userDetails will update
    }, []);

    return (
        <TrialContext.Provider value={{
            trialUsed,
            isTrialing,
            hasActiveSubscription,
            isTrialEnded,
            isTeamMemberNotOwner,
            eligibleForTrial,
            showTrialModal,
            closeTrialModal,
            isTrialModalOpen,
            trialHeaderDismissed,
            dismissTrialHeader,
            aboutTrialDismissed,
            notifyAboutTrialDismissed,
        }}>
            {children}
            {isTrialModalOpen && (
                <FreeTrialModal
                    open={isTrialModalOpen}
                    onClose={closeTrialModal}
                    onTrialStarted={handleTrialStarted}
                />
            )}
        </TrialContext.Provider>
    );
};

export const useTrialContext = (): TrialContextType => {
    const context = useContext(TrialContext);
    if (context === undefined) {
        throw new Error('useTrialContext must be used within a TrialProvider');
    }
    return context;
};

/** Safe version that returns null when outside provider */
export const useTrialContextSafe = (): TrialContextType | null => {
    const context = useContext(TrialContext);
    return context || null;
};
